/**
 * The whole reactive layer PadLab has. It exists instead of a framework because a
 * framework's scheduler can land a re-render between a `pointerdown` and the moment the
 * audio engine is told to play — this cannot, since nothing here ever defers a read.
 *
 * Dependency tracking is a current-observer stack: reading a signal while an effect or a
 * computed is running records the edge in both directions, so the next run can unsubscribe
 * from branches it no longer touches.
 */

type Listener<T> = (value: T) => void;

/** Anything that can be depended upon. */
interface Source {
  readonly observers: Set<Observer>;
}

/** Anything that re-runs when a source it read changes. */
interface Observer {
  /** Sources read during the last run, so the next run can drop stale edges. */
  readonly sources: Set<Source>;
  /** A dependency changed. Queue whatever this observer has to do. */
  notify(): void;
  /** Do it. Called by the flush loop, never straight from a source. */
  run(): void;
}

export interface ReadonlySignal<T> {
  readonly value: T;
  /** Reads without recording a dependency — for audio paths that must not re-run UI. */
  peek(): T;
  /** Calls back immediately with the current value, then on every change. */
  subscribe(listener: Listener<T>): () => void;
}

export interface Signal<T> extends ReadonlySignal<T> {
  value: T;
}

let currentObserver: Observer | null = null;
let batchDepth = 0;
let flushing = false;
const pending = new Set<Observer>();

/** Guards a cycle where observers keep dirtying each other for ever. */
const MAX_FLUSH_STEPS = 10_000;

function link(source: Source): void {
  if (currentObserver === null) return;
  source.observers.add(currentObserver);
  currentObserver.sources.add(source);
}

function unlink(observer: Observer): void {
  for (const source of observer.sources) source.observers.delete(observer);
  observer.sources.clear();
}

function enqueue(observer: Observer): void {
  pending.add(observer);
  if (batchDepth === 0) flush();
}

function flush(): void {
  if (flushing) return;
  flushing = true;
  let steps = 0;
  try {
    // Iterating a Set visits entries appended during iteration, so a computed that
    // propagates into an effect is handled in the same pass, in dependency order.
    for (const observer of pending) {
      pending.delete(observer);
      if (++steps > MAX_FLUSH_STEPS) {
        pending.clear();
        throw new Error('Signal update loop: an observer keeps invalidating its own dependencies.');
      }
      observer.run();
    }
  } finally {
    flushing = false;
  }
}

/**
 * Whether a computed's result is something an edit can change without changing the value
 * itself.
 *
 * Identity is only a sound "nothing actually changed" test for immutable values. PadLab's
 * open document is mutated in place and the change is announced through a separate
 * `revision` counter, so a computed that hands back a live object or array — the pad's
 * `SampleEdit`, the effect chain's node list, the current bank — holds the *same reference*
 * before and after the edit. Suppressing propagation on that comparison froze every view
 * derived from one: the wave editor's readouts, its effect rack and the Sound Lab's rack
 * and lock buttons only refreshed when the screen was rebuilt from scratch.
 *
 * A computed returning a primitive keeps the dedup, which is where it earns its keep — a
 * boolean or a number derived from a big object should not wake the DOM when the derived
 * value is unchanged.
 */
function isMutableReference(value: unknown): boolean {
  return value !== null && (typeof value === 'object' || typeof value === 'function');
}

function runTracked<T>(observer: Observer | null, fn: () => T): T {
  const previous = currentObserver;
  currentObserver = observer;
  try {
    return fn();
  } finally {
    currentObserver = previous;
  }
}

/**
 * Fans a change out to observers and plain subscribers inside an implicit batch, so one
 * write produces one flush no matter how many things were listening.
 */
function propagate(source: Source, listenerRunner: Observer | null, hasListeners: boolean): void {
  batchDepth++;
  try {
    for (const observer of [...source.observers]) observer.notify();
    if (hasListeners && listenerRunner !== null) enqueue(listenerRunner);
  } finally {
    batchDepth--;
    if (batchDepth === 0) flush();
  }
}

/** Delivers `subscribe` callbacks through the same queue as effects so batches coalesce. */
class ListenerRunner<T> implements Observer {
  readonly sources = new Set<Source>();
  constructor(
    private readonly listeners: Set<Listener<T>>,
    private readonly read: () => T,
  ) {}

  notify(): void {
    enqueue(this);
  }

  run(): void {
    if (this.listeners.size === 0) return;
    const value = this.read();
    for (const listener of [...this.listeners]) listener(value);
  }
}

class WritableSignal<T> implements Signal<T>, Source {
  readonly observers = new Set<Observer>();
  private readonly listeners = new Set<Listener<T>>();
  private readonly runner: ListenerRunner<T>;
  private current: T;

  constructor(initial: T) {
    this.current = initial;
    this.runner = new ListenerRunner(this.listeners, () => this.current);
  }

  get value(): T {
    link(this);
    return this.current;
  }

  set value(next: T) {
    if (Object.is(next, this.current)) return;
    this.current = next;
    propagate(this, this.runner, this.listeners.size > 0);
  }

  peek(): T {
    return this.current;
  }

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    listener(this.current);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

class ComputedSignal<T> implements ReadonlySignal<T>, Source, Observer {
  readonly observers = new Set<Observer>();
  readonly sources = new Set<Source>();
  private readonly listeners = new Set<Listener<T>>();
  private readonly runner: ListenerRunner<T>;
  private readonly compute: () => T;
  private current!: T;
  /** Value at the moment of invalidation — the yardstick for "did this actually change". */
  private snapshot!: T;
  private stale = true;

  constructor(compute: () => T) {
    this.compute = compute;
    this.runner = new ListenerRunner(this.listeners, () => this.evaluate());
  }

  get value(): T {
    link(this);
    return this.evaluate();
  }

  peek(): T {
    return this.evaluate();
  }

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    listener(this.evaluate());
    return () => {
      this.listeners.delete(listener);
    };
  }

  notify(): void {
    if (this.stale) return;
    this.stale = true;
    this.snapshot = this.current;
    // Only worth re-deriving eagerly when someone is watching; an unobserved computed
    // stays lazy and costs nothing until it is read.
    if (this.observers.size > 0 || this.listeners.size > 0) enqueue(this);
  }

  run(): void {
    // `snapshot`, not `current`: a read between the invalidation and this flush has
    // already refreshed `current`, and comparing against it would swallow the change.
    const next = this.evaluate();
    if (Object.is(next, this.snapshot) && !isMutableReference(next)) return;
    propagate(this, this.runner, this.listeners.size > 0);
  }

  private evaluate(): T {
    if (!this.stale) return this.current;
    unlink(this);
    this.current = runTracked(this, this.compute);
    this.stale = false;
    return this.current;
  }
}

class Effect implements Observer {
  readonly sources = new Set<Source>();
  private readonly fn: () => void;
  private disposed = false;

  constructor(fn: () => void) {
    this.fn = fn;
    this.run();
  }

  notify(): void {
    if (!this.disposed) enqueue(this);
  }

  run(): void {
    if (this.disposed) return;
    unlink(this);
    runTracked(this, this.fn);
  }

  dispose(): void {
    this.disposed = true;
    pending.delete(this);
    unlink(this);
  }
}

export function signal<T>(initial: T): Signal<T> {
  return new WritableSignal(initial);
}

export function computed<T>(fn: () => T): ReadonlySignal<T> {
  return new ComputedSignal(fn);
}

/** Runs `fn` now, and again whenever anything it read changes. Returns a disposer. */
export function effect(fn: () => void): () => void {
  const instance = new Effect(fn);
  return () => instance.dispose();
}

/** Collapses every change made inside `fn` into one notification pass. Nests safely. */
export function batch<T>(fn: () => T): T {
  batchDepth++;
  try {
    return fn();
  } finally {
    batchDepth--;
    if (batchDepth === 0) flush();
  }
}

/** Reads inside `fn` do not become dependencies of the enclosing effect or computed. */
export function untracked<T>(fn: () => T): T {
  return runTracked(null, fn);
}

export function isSignal(value: unknown): value is ReadonlySignal<unknown> {
  return value instanceof WritableSignal || value instanceof ComputedSignal;
}

export function isWritableSignal(value: unknown): value is Signal<unknown> {
  return value instanceof WritableSignal;
}
